import { useEffect, useRef } from 'react';
import { useLocation, useMatches } from 'react-router';
import { useAuthStore } from '@/stores/authStore';
import { useIsSimulating, useSimulationStore } from '@/stores/simulationStore';
import { STARTADRES_OPGERUIMD } from '@/components/tabel/useStartadres';
import { isAnalyticsRoutePattern, type AnalyticsRoutePattern } from '@xxtract/analytics-contract';

const ENDPOINT = '/api/v1/analytics/pageviews';
const MAX_BUFFERED_EVENTS = 100;
const MAX_BATCH_SIZE = 50;
const FLUSH_INTERVAL_MS = 15_000;

export interface TrackedPageView { route: AnalyticsRoutePattern; }

interface AnalyticsHandle { analyticsPattern?: string; }

export function getRoutePattern(matches: ReturnType<typeof useMatches>, pathname: string): AnalyticsRoutePattern | null {
  const configured = [...matches].reverse().find((match) => (match.handle as AnalyticsHandle | undefined)?.analyticsPattern)?.handle as AnalyticsHandle | undefined;
  if (configured?.analyticsPattern && isAnalyticsRoutePattern(configured.analyticsPattern)) return configured.analyticsPattern;
  // Only router metadata is authoritative. Unknown/not-found locations are never recorded.
  void pathname;
  return null;
}

function isOwnEndpoint(url: string): boolean {
  const parsed = new URL(url, window.location.origin);
  return parsed.origin === window.location.origin && parsed.pathname === ENDPOINT;
}

export function AnalyticsTracker() {
  const location = useLocation();
  const matches = useMatches();
  const isSimulating = useIsSimulating();
  const buffer = useRef<TrackedPageView[]>([]);
  const lastNavigation = useRef<string | null>(null);
  const lastPathname = useRef<string | null>(null);
  const timer = useRef<number | null>(null);
  const retryTimer = useRef<number | null>(null);
  const inFlight = useRef(false);
  const retryCount = useRef(0);
  const flushRef = useRef<() => Promise<void>>(async () => undefined);
  const epoch = useRef(0);
  const excludeNextPostSimulationLocation = useRef(false);

  const invalidateDelivery = () => {
    epoch.current += 1;
    buffer.current = [];
    retryCount.current = 0;
    if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
    retryTimer.current = null;
  };

  const flush = async () => {
    if (inFlight.current || retryTimer.current !== null || buffer.current.length === 0 || !useAuthStore.getState().isAuthenticated || useSimulationStore.getState().isSimulating) return;
    inFlight.current = true;
    const deliveryEpoch = epoch.current;
    const events = buffer.current.splice(0, MAX_BATCH_SIZE);
    const target = new URL(ENDPOINT, window.location.origin).toString();
    if (!isOwnEndpoint(target)) { inFlight.current = false; return; }
    let retryAfterMs = 0;
    try {
      const response = await fetch(target, {
        method: 'POST', credentials: 'same-origin', keepalive: true,
        headers: { 'Content-Type': 'application/json', ...(useAuthStore.getState().accessToken ? { Authorization: `Bearer ${useAuthStore.getState().accessToken}` } : {}) },
        body: JSON.stringify({ events }),
      });
      if (!response.ok) {
        if (response.status === 400 || response.status === 422) return;
        if (response.status === 401 || response.status === 403) { buffer.current = []; retryCount.current = 0; return; }
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const seconds = retryAfter ? Number(retryAfter) : NaN;
          const date = retryAfter && Number.isNaN(seconds) ? Date.parse(retryAfter) - Date.now() : NaN;
          retryAfterMs = Math.min(Math.max(Number.isFinite(seconds) ? seconds * 1_000 : Number.isFinite(date) ? date : 1_000, 1_000), 60_000);
        } else if (response.status < 500) return;
        throw new Error(`analytics-${response.status}`);
      }
      retryCount.current = 0;
    } catch {
      if (deliveryEpoch === epoch.current && useAuthStore.getState().isAuthenticated && !useSimulationStore.getState().isSimulating) {
        if (retryCount.current >= 5) { retryCount.current = 0; return; }
        buffer.current = [...events, ...buffer.current].slice(0, MAX_BUFFERED_EVENTS);
        retryCount.current += 1;
        if (retryTimer.current === null) retryTimer.current = window.setTimeout(() => { retryTimer.current = null; void flush(); }, retryAfterMs || Math.min(1_000 * 2 ** (retryCount.current - 1), 30_000));
      }
    } finally { inFlight.current = false; }
  };
  flushRef.current = flush;

  useEffect(() => {
    if (isSimulating || !useAuthStore.getState().isAuthenticated) {
      lastPathname.current = location.pathname;
      if (isSimulating) excludeNextPostSimulationLocation.current = true;
      invalidateDelivery();
      lastNavigation.current = null;
      return;
    }
    if (excludeNextPostSimulationLocation.current) {
      lastPathname.current = location.pathname;
      excludeNextPostSimulationLocation.current = false;
      lastNavigation.current = location.key;
      return;
    }
    // spec-tabelfilters §7: startparameters uit het adres halen is geen nieuwe paginaweergave. Alleen op
    // hetzelfde pad, zodat later terugkeren naar die geschiedenisregel wél telt.
    const opgeruimd = (location.state as Record<string, unknown> | null)?.[STARTADRES_OPGERUIMD] === true && location.pathname === lastPathname.current;
    lastPathname.current = location.pathname;
    if (opgeruimd) {
      lastNavigation.current = location.key;
      return;
    }
    const route = getRoutePattern(matches, location.pathname);
    if (!route || location.key === lastNavigation.current) return;
    lastNavigation.current = location.key;
    buffer.current = [...buffer.current, { route }].slice(-MAX_BUFFERED_EVENTS);
  }, [isSimulating, location.key, location.pathname, location.state, matches]);

  useEffect(() => {
    const runFlush = () => { void flushRef.current(); };
    timer.current = window.setInterval(runFlush, FLUSH_INTERVAL_MS);
    const onVisibility = () => { if (document.visibilityState === 'hidden') runFlush(); };
    window.addEventListener('pagehide', runFlush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (timer.current !== null) window.clearInterval(timer.current);
      if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
      retryTimer.current = null;
      window.removeEventListener('pagehide', runFlush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => useSimulationStore.subscribe((state, previous) => {
    if (state.isSimulating && !previous.isSimulating) {
      excludeNextPostSimulationLocation.current = true;
      invalidateDelivery();
      lastNavigation.current = null;
    }
    if (!state.isSimulating && previous.isSimulating) {
      // This synchronous latch wins even when stopSimulation and router
      // navigation occur in the same tick, excluding exactly that redirect.
      excludeNextPostSimulationLocation.current = true;
      invalidateDelivery();
      lastNavigation.current = null;
    }
  }), []);

  useEffect(() => useAuthStore.subscribe((state, previous) => {
    if (!state.isAuthenticated && previous.isAuthenticated) {
      // Uitloggen stopt de simulatie; die stop mag de eerste pagina na opnieuw inloggen niet overslaan.
      excludeNextPostSimulationLocation.current = false;
      invalidateDelivery();
    }
  }), []);

  return null;
}
